/**
 * Scoring formulas for TypingWhiz.
 * These are the ONLY definitions of WPM, accuracy, and error counts.
 * All features (test, lesson, practice, story, game) MUST import from here.
 *
 * Standard conventions used:
 *   - 1 "word" = 5 characters (the de-facto standard used by Monkeytype,
 *     10fastfingers, TypingTest.com, and most competitive typing sites).
 *   - Gross WPM counts every typed character.
 *   - Net WPM counts only correct characters, so mistakes reduce the score.
 *   - Accuracy is correct ÷ total typed × 100, computed over all keystrokes
 *     the user has committed (backspace does not retroactively mark a wrong
 *     keystroke as correct).
 *
 * @module typing-engine/formulas
 */

export const CHARS_PER_WORD = 5;
export const SECONDS_PER_MINUTE = 60;

/**
 * Gross words per minute.
 * @param {number} totalTyped - All committed characters (correct + incorrect).
 * @param {number} elapsedSeconds - Active elapsed seconds (> 0).
 * @returns {number} Gross WPM rounded to 2 decimals.
 */
export function grossWpm(totalTyped, elapsedSeconds) {
  if (!Number.isFinite(elapsedSeconds) || elapsedSeconds <= 0) return 0;
  if (!Number.isFinite(totalTyped) || totalTyped < 0) return 0;
  const minutes = elapsedSeconds / SECONDS_PER_MINUTE;
  return round2((totalTyped / CHARS_PER_WORD) / minutes);
}

/**
 * Net words per minute.
 * @param {number} correctTyped - Characters typed correctly.
 * @param {number} elapsedSeconds - Active elapsed seconds (> 0).
 * @returns {number} Net WPM rounded to 2 decimals.
 */
export function netWpm(correctTyped, elapsedSeconds) {
  if (!Number.isFinite(elapsedSeconds) || elapsedSeconds <= 0) return 0;
  if (!Number.isFinite(correctTyped) || correctTyped < 0) return 0;
  const minutes = elapsedSeconds / SECONDS_PER_MINUTE;
  return round2((correctTyped / CHARS_PER_WORD) / minutes);
}

/**
 * Accuracy as a percentage (0–100). Returns 100 when nothing has been typed.
 * @param {number} correctTyped
 * @param {number} totalTyped
 * @returns {number} Accuracy rounded to 2 decimals.
 */
export function accuracy(correctTyped, totalTyped) {
  if (!Number.isFinite(totalTyped) || totalTyped <= 0) return 100;
  if (!Number.isFinite(correctTyped) || correctTyped < 0) return 0;
  const clamped = Math.min(correctTyped, totalTyped);
  return round2((clamped / totalTyped) * 100);
}

/**
 * Rounds a number to two decimal places without floating-point drift.
 * @param {number} n
 * @returns {number}
 */
export function round2(n) {
  return Math.round(n * 100) / 100;
}

/**
 * Predicate: is this attempt eligible for competitive ranking?
 * Rules (consistent across the app and enforced again server-side):
 *   - duration >= 60 seconds
 *   - netWpm > 0
 *   - accuracy between 0 and 100
 *   - at least one correct character
 * @param {{ durationSeconds: number, netWpm: number, accuracy: number, correctChars: number }} attempt
 * @returns {boolean}
 */
export function isLeaderboardEligible(attempt) {
  if (!attempt) return false;
  const { durationSeconds, netWpm, accuracy, correctChars } = attempt;
  return (
    Number.isFinite(durationSeconds) &&
    durationSeconds >= 60 &&
    Number.isFinite(netWpm) &&
    netWpm > 0 &&
    Number.isFinite(accuracy) &&
    accuracy >= 0 &&
    accuracy <= 100 &&
    Number.isFinite(correctChars) &&
    correctChars > 0
  );
}